import LinkWithQuery from 'src/app/components/Elements/LinkWithQuery';
import { clearRxHIPAA, clearRxPrivacy, hipaa, privacy } from 'src/app/router/paths';

import { Main, TabbedList, TopLevelOrderedList } from './styles';

const address = 'Capsule, 122 W 146th, New York, NY 10039-3802';

export const TermsConditions = (props: { isClearRxPage: boolean }) => {
  const isClearRxPage = props.isClearRxPage || false;
  const privacyPath = isClearRxPage ? clearRxPrivacy.toPath() : privacy.toPath();
  const hipaaPath = isClearRxPage ? clearRxHIPAA.toPath() : hipaa.toPath();
  return (
    <Main>
      <h1 className="h3">Capsule Terms of Service</h1>
      <h2 className="font-up-2">Effective Date: October 25th, 2024</h2>
      <h2 className="font-up-2">Hello, Dear!</h2>

      <p>
        Welcome to Capsule, a new kind of pharmacy! Before you use our delightful pharmacy services you are required to
        carefully read and agree to these Terms of Service (these &ldquo;Terms&rdquo;) as they have important
        information about your use of Capsule’s pharmacy services, website, mobile application, products, and other
        technology platforms (collectively, the &ldquo;Service&rdquo;). Your use of and access to the Services are also
        subject to our <LinkWithQuery to={privacyPath}>privacy policy</LinkWithQuery> (the &ldquo;Privacy Policy&rdquo;)
        and{' '}
        <LinkWithQuery to={hipaaPath}>
          Health Insurance Portability and Accountability Act of 1996 Notice of Privacy Practices
        </LinkWithQuery>{' '}
        (&ldquo;HIPAA Notice&rdquo;), which contain important information about how Capsule protects your personal
        information. If you’ve any questions about these Terms, the Privacy Policy, or the HIPAA Notice, please reach
        out to us at legal@capsule.com before you use or otherwise access any of the Services.
      </p>

      <p>
        Note that when we say &ldquo;us&ldquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;Capsule&rdquo; in these
        Terms, we mean Capsule Corporation and its subsidiaries and when we say &ldquo;you&rdquo; or &ldquo;your&rdquo;
        in these Terms, we mean a customer of any of the Services, including a customer who has been added as a
        dependent to another customer’s account.
      </p>

      <p>
        PLEASE NOTE THAT THESE TERMS CONTAIN A MANDATORY ARBITRATION OF DISPUTES PROVISION THAT REQUIRES THE USE OF
        ARBITRATION ON AN INDIVIDUAL BASIS TO RESOLVE DISPUTES, RATHER THAN JURY TRIALS OR CLASS ACTIONS.
      </p>

      <TopLevelOrderedList>
        <li>
          <h2 className="font-up-2">1. Accepting these Terms</h2>
          <p>
            By using or accessing the Services, you agree to be legally bound by these Terms. If you don’t agree to
            these Terms, you shouldn’t and aren’t permitted to use or access any of the Services.
          </p>
          <p>
            If you are a new customer, these Terms are effective on the date of your first use of the Services. If you
            are an existing customer, these Terms are effective on the Effective Date.
          </p>
        </li>

        <li>
          <h2 className="font-up-2">2. Modifications to the Terms and the Services</h2>
          <p>
            <strong>These Terms May Change.</strong> We may change these Terms at any time. However, we’ll give you
            prior notice of any material changes by placing a notice on the Services, sending you an email, or some
            other manner, and we’ll let you know when the modified Terms will become effective. Your use of or access to
            the Services after the new Effective Date will be considered assent to the new Terms.
          </p>
          <p>
            <strong>The Services May Change.</strong> You understand that the Services may change over time. As we
            strive to provide you with the most delightful pharmacy experience, we may add new features to existing
            Services or new types of Services, and these Terms will be applicable to these new features or Services. We
            may also suspend, limit, or remove certain features or types of Services, or remove Content (as defined
            below) from the Services, and we reserve the right to take such actions in our sole determination and
            without notice to you.
          </p>
        </li>

        <li>
          <h2 className="font-up-2">3. Your Use of the Services</h2>
          <p>
            We’re thrilled that you’ve decided to use Capsule! We have some ground rules to make sure your use of the
            Services doesn’t violate any laws and is as caring and kind as the Services we provide to you.
          </p>
          <p>Specifically, you agree that:</p>

          <TabbedList>
            <li>
              You will not use the Services if you’re less than 18 years of age (but a parent or guardian may use the
              Services on your behalf if you’re under 18); unless you reside in a state that permits minors to access
              pharmacy services without parental or guardian consent;
            </li>

            <li>
              If you’re using the Services on behalf of another customer, including by adding such customer as a
              dependent to your account, you will obtain their consent prior to using the Services on their behalf;
            </li>

            <li>You will not use the Services for any unlawful purpose;</li>

            <li>
              You will not submit inaccurate, incomplete, or out-of-date information or commit fraud or falsify
              information in connection with your use of the Services;
            </li>

            <li>
              You will not attempt to obtain the password, account, or other security information of any other customer;
            </li>

            <li>
              You will not otherwise copy, reproduce, display, duplicate, sell, publish, post, license, rent,
              distribute, modify, translate, adapt, reverse-engineer, or create derivative works of any part of any of
              the Services;
            </li>

            <li>
              You will not engage in data mining or similar data gathering or extraction activities or retrieve data or
              other content from the Services for purposes of creating or compiling that data or content for any
              purpose;
            </li>

            <li>
              You will not interrupt or attempt to interrupt the Services’ operation or any other person’s use of the
              Services in any way (including by hacking or defacing any portion of the Services or by providing any
              material that contains any viruses or other harmful computer code);
            </li>

            <li>
              You will not sign up for an account or otherwise use the Services if you’re located outside of the United
              States;
            </li>

            <li>
              You will not use or interact with the Services in any manner that adversely affects our interests or the
              interests of a third party, including any use or interaction that infringes or violates the intellectual
              property rights of us or any third party;
            </li>

            <li>
              You will not use or interact with the Services in any manner (including through the materials that you
              submit to the Services) that is unlawful, harmful, or otherwise objectionable as we determine in our
              discretion; and
            </li>

            <li>
              You will not threaten, harass, discriminate against, or otherwise act inappropriately towards any of our
              employees or service providers.
            </li>
          </TabbedList>
          <p>
            You understand that we may suspend or terminate your ability to use any of the Services if we believe that
            you’re breaking any of these rules.
          </p>
          <p>
            When you use our pharmacy Services, you understand that our licensed pharmacists review each order and
            exercise their professional judgment in determining whether to fill each order. In certain circumstances, a
            pharmacist may decide not to fill a specific order and that decision will be communicated to you.
          </p>
        </li>

        <li>
          <h2 className="font-up-2">4. Your Account and Account Information</h2>
          <p>
            You may be required to create an account in connection with your use of the Services. You may also add
            family members to your account so you can use the Services on their behalf. To maintain the security and
            integrity of your account and account information, you agree that:
          </p>

          <TabbedList>
            <li>
              You will provide us with accurate, current, and complete information about yourself and any family members
              associated with your account;
            </li>
            <li>You will promptly update all account information to keep it accurate, current, and complete;</li>
            <li>
              You will take any and all actions to protect the confidentiality of your account information, including
              your password;
            </li>
            <li>You will be responsible for any and all activities that occur in connection with your account;</li>
            <li>
              You will immediately notify us of any unauthorized use of either your password or account or any other
              breach of security; and
            </li>
            <li>You will not permit other parties to access the Services using your account.</li>
          </TabbedList>
        </li>

        <li>
          <h2 className="font-up-2">5. Privacy</h2>
          <p>
            Your account information, health information, and all other information that you provide to us is subject to
            our <LinkWithQuery to={privacyPath}>Privacy Policy</LinkWithQuery> and our{' '}
            <LinkWithQuery to={hipaaPath}>HIPAA Notice</LinkWithQuery> and by using the Services, you agree that you’ve
            read the Privacy Policy and HIPAA Notice. You agree to grant us a royalty-free, perpetual, sublicensable,
            irrevocable, and worldwide license to use the information you provide to us in accordance with our Privacy
            Policy and HIPAA Notice in order to provide the Services.
          </p>
        </li>

        <li>
          <h2 className="font-up-2">6. Your Rights in the Services</h2>
          <p>
            <strong>We Own the Services.</strong> You understand and agree that the Services and all information, data,
            software, photographs, graphics, videos, text, images, typefaces, sounds and other material (collectively
            “Content”) contained on the Services are owned by Capsule and are protected by intellectual property rights
            and laws.
          </p>
          <p>
            <strong>We Grant You a License to the Services.</strong> To enable you to use the Services, we grant you a
            limited, non-exclusive, non-transferable, non-sublicensable, revocable license to access and use the
            Services for your own personal, non-commercial purposes and, if applicable, the personal, non-commercial use
            of any family members who have given you permission to access the Services on their behalf and who are
            associated with your account. This right is conditioned upon you continuing to comply with these Terms.
            Except as expressly provided in these Terms, no part of the Services and no Content may be copied,
            reproduced, republished, uploaded, posted, publicly displayed, encoded, translated, transmitted or
            distributed in any way to any other computer, server, web site or other medium for publication or
            distribution or for any commercial enterprise without our express prior written consent.
          </p>
          <p>
            <strong>We Appreciate Your Feedback.</strong> Capsule welcomes feedback about your use of the Services so we
            can make the Services better for you and all our customers. Please note that any feedback you provide will
            be non-confidential, owned by us, and freely used at our discretion.
          </p>
        </li>

        <li>
          <h2 className="font-up-2">7. Your Communications with Capsule</h2>
          <p>
            <strong>You Consent to Receive Our Electronic Communications.</strong> You consent to receive communications
            (including emails, texts, phone calls, and notices and messages sent through the Services) from or on behalf
            of us to any email address, phone number, or mobile device associated with your account or otherwise
            directly or indirectly provided to us. This consent applies to marketing and non-marketing communications
            from or on behalf of Capsule to you. Consent to receive marketing communications is not a condition to use
            the Services. Consent to receiving electronic communications from us may be revoked at any time by calling
            (888) 910-1808 or contacting our Pharmacy Experience team through our mobile application, although you may
            not be able to opt out of necessary transaction communications from us, such as order and delivery
            confirmations. You may opt out of text messages from us by replying with STOP to any of our text messages;
            future text messages from us will be automatically blocked by your mobile carrier.
          </p>
          <p>
            <strong>We May Text You.</strong> Capsule may use SMS (Short Message Service), MMS (Multimedia Message
            Service), and similar technologies to communicate with you using text messages. The frequency of text
            messages we send to you may vary. You’ll typically receive text messages when we have necessary information
            for you, and whoever has access to the mobile phone or carrier account may also be able to see this
            information. Data obtained from you in connection with our SMS service may include your cell phone number,
            your provider's name, and the date, time, and content of your messages. We don’t impose a separate charge
            for text messages, but rates may vary depending on your agreement with your mobile carrier. Capsule won’t be
            responsible for any text messaging or wireless charges incurred by you or by a person that has access to
            your device or telephone number. We may change or discontinue any text message programs without notice or
            liability to you and we reserve the right to cease delivery of text messages at any time.
          </p>
          <p>
            <strong>Acknowledgement of Privacy Risks.</strong> By using the Services, you understand that electronic
            communications are never completely private or secure and certain communications have inherent privacy
            risks. You acknowledge that any communications you send to Capsule may be read or intercepted by others,
            even if a particular communication is encrypted or purports to be encrypted. Such communications, which may
            contain your health-related information, could be accessed by an unauthorized party, intercepted, or altered
            without your knowledge or authorization.
          </p>
          <p>
            <strong>Telephone Counseling Waiver.</strong> By accepting these Terms you indicate that, by default, you
            don’t wish to be contacted by telephone for medication counseling. You may, however, call a licensed
            pharmacist at any time during business hours to ask questions about your medications by telephone at (888)
            910-1808.
          </p>
        </li>

        <li>
          <h2 className="font-up-2">8. Your Payment Obligations; No Refunds; Order Delays</h2>
          <p>
            <strong>We May Use a Third-Party Payment Processor.</strong> Capsule may use a third-party payment processor
            to bill you through a payment account linked to your Capsule account. The processing of payments will be
            subject to the terms, conditions, and privacy policies of our payment processor in addition to these Terms.
            In that case, our payment processor - and not us - stores your payment method information.
          </p>
          <p>
            <strong>Your Responsibility for Payment.</strong> You understand and agree that you’re responsible for any
            copays set by your insurance plan, any payment for cash purchases for prescriptions and other products, and
            any other fees associated with any of our Services. You agree to pay us all charges associated with the
            requested Services at the prices then in effect and you authorize us, through our payment processor, to
            charge your selected payment method. If there is an issue charging your selected payment method, we’re
            permitted to charge any other valid payment method associated with your account. If there is no other valid
            payment method, we’ll attempt to contact you. However, in the interim, we may refrain from providing certain
            Services to you until the payment issue is resolved.
          </p>
          <p>
            <strong>No Refunds on Delivered Medication.</strong> Due to pharmacy rules, we are unable to accept returns
            of delivered medication. All medication purchases are final sale and no refunds will be issued.
          </p>
          <p>
            <strong>Your Order May Be Delayed.</strong> We do our best to provide you with your medication when you’ve
            requested it. However, there may be exceptions that cause your medication order to be delayed, and we’ll
            make you aware of those exceptions as they arise so you can reschedule your order or obtain your medication
            from another pharmacy.
          </p>
        </li>

        <li>
          <h2 className="font-up-2">
            9. Health-Related Content; Digital Medication Guides; Prescription Renewals and Coupons
          </h2>
          <p>
            <strong>Health-Related Content is Not a Substitute for Medical Advice.</strong> Any health-related Content
            provided on the Services is not intended to be a substitute for professional medical advice, diagnoses, or
            recommendations about medical treatment. Capsule provides counseling on medications, as a typical pharmacy
            would, through our licensed pharmacists. However, you should always check product information (including
            package inserts) regarding dosage, precautions, warnings, interactions and contraindications before
            administering or using any device, drug, herb, vitamin, or supplement. You expressly acknowledge and agree
            that Capsule is not responsible or liable for the results of any decisions made based on your use of
            health-related Content or the Services.
          </p>
          <p>
            <strong>Contact Health Care Professionals for Important Questions.</strong> If you have any questions about
            a medical condition, medicine, or prescription, contact one of our licensed pharmacists, your physician, or
            another health care provider. Never disregard medical advice or delay in seeking it because of something
            you’ve read on the Services.{' '}
            <strong>IN AN EMERGENCY, CALL 911 OR YOUR LOCAL EMERGENCY ASSISTANCE NUMBER.</strong>
          </p>
          <p>
            <strong>You Consent to Receive Digital Medication Guides.</strong> If you live in a state where digital
            medication guides are permitted by law, you may receive - and by accepting these Terms, you consent to
            receive - a digital medication guide with any prescription order. However, if you would prefer a physical
            copy of medication guide, please feel free to ask us for one at any time.
          </p>

          <p>
            <strong>You Consent to Provider Contact for Prescription Renewals.</strong> You agree that we may contact
            your provider in order to obtain a renewal of any of your prescriptions. If you don’t want us to contact
            your provider for prescription renewals, please let us know at any time.
          </p>
          <p>
            <strong> You Authorize Us to Apply Coupons on Your Behalf.</strong> In order to make your medication more
            affordable, you agree that we may complete the attestation or application associated with a coupon for your
            medication if you are eligible for such coupon. If you don’t want us to apply coupons on your behalf, please
            let us know at any time.
          </p>
        </li>

        <li>
          <h2 className="font-up-2">10. Disclaimers and Indemnification</h2>
          <p>
            <strong>No Responsibility for Third Parties.</strong> The Services may contain links to third-party websites
            or services that are not owned or controlled by Capsule. You understand and agree that you’re responsible
            for reviewing and complying with the applicable terms that govern your use of third-party websites or
            services. Capsule will not have any responsibility for your use of or interactions with these websites or
            services.
          </p>
          <p>
            <strong>The Services are Provided Without Warranties.</strong> THE SERVICES AND CONTENT ARE PROVIDED ON AN
            “AS-IS” AND “AS-AVAILABLE” BASIS, WITHOUT REPRESENTATIONS OR WARRANTIES OF ANY KIND. TO THE FULLEST EXTENT
            PERMITTED BY LAW, CAPSULE DISCLAIMS ALL EXPRESS OR IMPLIED REPRESENTATIONS AND WARRANTIES REGARDING ANY
            SERVICE AND ANY CONTENT AVAILABLE ON OR ACCESSIBLE THROUGH THE SERVICES, INCLUDING, WITHOUT LIMITATION, ANY
            WARRANTIES OF ACCURACY, TITLE, NON-INFRINGEMENT, MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE.
            CAPSULE DISCLAIMS ANY AND ALL LIABILITY FOR THE ACTS, OMISSIONS, AND CONDUCT OF ANY THIRD PARTIES IN
            CONNECTION WITH OR RELATED TO YOUR USE OF THE SERVICES. YOUR SOLE REMEDY AGAINST CAPSULE FOR DISSATISFACTION
            WITH ANY SERVICE OR ANY CONTENT IS TO STOP USING THE SERVICES.
          </p>
          <p>
            <strong>Our Liability to You May Be Limited.</strong> NEITHER CAPSULE NOR ANY OF OUR DIRECTORS, EMPLOYEES,
            AGENTS, PARTNERS, SUPPLIERS, LICENSORS OR CONTENT PROVIDERS WILL BE LIABLE TO YOU (OR TO ANY THIRD PARTY
            CLAIMING UNDER OR THROUGH YOU) FOR (I) ANY INDIRECT, PUNITIVE, INCIDENTAL, EXEMPLARY OR CONSEQUENTIAL
            DAMAGES, INCLUDING FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, ARISING FROM YOUR
            USE OF, OR INABILITY TO USE THE SERVICES OR THE CONTENT, OR (II) ANY DIRECT DAMAGES IN EXCESS (IN THE
            AGGREGATE) OF THE GREATER OF (A) FEES PAID TO US BY YOU FOR THE SERVICES DURING THE IMMEDIATELY PREVIOUS
            THREE (3) MONTH PERIOD OR (B) $1000.00. THE FOREGOING LIMITATIONS APPLY EVEN IF CAPSULE HAS BEEN ADVISED OF
            OR SHOULD HAVE KNOWN OF THE POSSIBILITY OF SUCH DAMAGES. IN JURISDICTIONS THAT DO NOT ALLOW THE EXCLUSION OR
            LIMITATION OF LIABILITY OF CERTAIN DAMAGES, OUR LIABILITY SHALL BE LIMITED IN ACCORDANCE HEREIN TO THE
            FULLEST EXTENT PERMITTED BY LAW.
          </p>
          <p>
            <strong>You Agree to Indemnify Us.</strong> You understand that you’ll be responsible to us if a third party
            makes a claim against us due to your use of the Services or your violation of these Terms. You agree to
            defend, indemnify and hold Capsule, its affiliates, and each of our and their respective officers,
            directors, affiliates, employees, contractors, suppliers, agents, and representatives (the “Capsule
            Parties”) harmless from any third-party demands, loss, liability, claims, or expenses (including reasonable
            attorneys’ fees) (a “Third-Party Claim”) arising out of or in connection with your use of the Services or
            Content or the violation of these Terms by you or anyone using your account. We reserve the right to assume
            the exclusive defense and control of any Third-Party Claim, in which event and upon our request, you’ll
            assist and cooperate with us in asserting any available defenses. In the event of a Third-Party Claim, we’ll
            attempt to provide notice of the Third-Party Claim to the contact information associated with your account.
          </p>
        </li>

        <li>
          <h2 className="font-up-2">11. Termination</h2>
          <p>
            <strong>We May Terminate.</strong> We reserve the right to suspend or terminate your account and access to
            and use of the Services, without notice or liability to you or any third party, if we believe, in our sole
            determination, that you are violating these Terms or for any other lawful reason or no reason - though
            generally we’ll terminate your account only if you’re misusing or abusing the Services in some way.
          </p>
          <p>
            <strong>You May Terminate.</strong> If you want to terminate these Terms and stop using the Services, you
            may let us know at any time and for any reason (but we’ll be sad to see you go!).
          </p>
          <p>
            <strong>Effect of Termination.</strong> Upon termination of your use of and access to the Services, these
            Terms will automatically terminate with respect to you, although this will not relieve you of any of your
            obligations that arose or accrued prior to termination. Please note that certain provisions of these Terms
            that are intended to survive termination of our relationship (including provisions regarding
            indemnification, limitation of liability, and dispute resolution) will survive termination. If you decide to
            use the Services again after terminating your account, you will at that point once again be subject to these
            Terms.
          </p>
        </li>

        <li>
          <h2 className="font-up-2">12. Resolving Disputes</h2>
          <p>
            PLEASE READ THIS SECTION CAREFULLY – IT MAY SIGNIFICANTLY AFFECT YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT TO
            FILE A LAWSUIT IN COURT.
          </p>
          <p>
            <strong>New York Law Governs.</strong> Except as specifically set forth in these Terms, these Terms are
            governed by and will be construed under the laws of the State of New York. Any dispute arising from or
            relating to the subject matter of these Terms shall be finally decided either through arbitration as
            described below or, if such dispute is not subject to arbitration, by the state or federal courts located in
            New York, New York.
          </p>
          <p>
            <strong>No Class Actions.</strong> You understand and agree that you may only resolve disputes with us on an
            individual basis and may not bring a claim as a plaintiff or a class member in a class, consolidated, or
            representative action. You agree that class arbitrations, class actions, private attorney general actions,
            and consolidation with other arbitrations are not permitted.
          </p>
          <p>
            <strong>We Both Agree to Arbitrate.</strong>
          </p>

          <TabbedList>
            <li>
              <u>Let's Try To Sort Things Out First</u>. We want to address your concerns without needing a formal legal
              case. Before filing a claim against us in connection with these Terms or the Services, you agree to try to
              resolve the dispute informally by contacting legal@capsule.com. We’ll try to resolve the dispute
              informally by contacting you via email. If a dispute is not resolved within 30 days of submission, you or
              we may bring a formal arbitration proceeding as described below.
            </li>
            <li>
              <u>We Both Agree To Arbitrate</u>. You and we agree to resolve any claims relating to these Terms or the
              Services through final and binding arbitration, except as set forth under Exceptions to Agreement to
              Arbitrate below or where prohibited by law. Arbitration shall be held in the state in which you reside.
              For residents outside the United States, arbitration shall be initiated in New York, New York, or any
              other location to which the parties agree, and you and we agree to submit to the personal jurisdiction of
              any or state or federal court in the state of New York to compel arbitration, stay proceedings pending
              arbitration, or to confirm, modify, vacate, or enter judgment on the award entered by the arbitrator.
            </li>
            <li>
              <u>Opt-out of Agreement to Arbitrate.</u> You can decline this agreement to arbitrate by sending a letter
              to us that must be postmarked within 30 days of your first acceptance of these Terms. The letter must be
              dated and specify your first and last name and mailing address, and it must explain that you’re opting out
              of this arbitration provision. The letter should be sent to {address} (Attention: Legal). Your decision to
              decline arbitration will not affect in any way the scope or quality of the Services that you receive. If
              you don’t decline this agreement to arbitrate within 30 days, it will apply to all subsequent versions of
              these Terms, as they may be amended from time to time.
            </li>
            <li>
              <u>Arbitration Procedures and Effect</u>. The arbitration shall be conducted by a single neutral
              arbitrator, governed by the Comprehensive Arbitration Rules & Procedures of JAMS that are in effect at the
              time the arbitration is initiated, available at www.jamsadr.com or by calling JAMS at 1-800-352-5276,
              (referred to as the “JAMS Rules”) and under the rules set forth in these Terms. We may also mutually agree
              to have the arbitration conducted by telephone or written submissions. You and we agree to allow for the
              discovery or exchange of non-privileged information relevant to the dispute. An arbitrator's award will
              consist of a written statement stating the disposition of each claim. The award will also provide a
              concise written statement of the essential findings and conclusions on which the award is based.
            </li>
            <li>
              <u>Arbitration and Attorney's Fees</u>. The JAMS Rules will govern payment of filing fees and arbitration
              fees. In accordance with JAMS’ Policy on Consumer Arbitrations Pursuant to Pre-Dispute Clauses Minimum
              Standards of Procedural Fairness, if you initiate arbitration against us, the only arbitration fee
              required to be paid by you is $250. All other arbitration costs must be borne by us, including any
              remaining JAMS Case Management Fee and all professional fees for the arbitrator’s services. When we’re the
              claiming party initiating an arbitration against you, we will pay all costs associated with the
              arbitration. You and we agree that the arbitrator must follow these Terms and can award the same damages
              and relief as a court, including injunctive or other equitable relief and costs and attorneys’ fees.
              Notwithstanding the foregoing, and to the extent permitted by the JAMS’ Minimum Standards for Arbitration
              Procedures, you and we agree that the prevailing party may and will only seek any attorneys’ fees and
              expert witness costs unless the arbitrator finds that a claim or defense was frivolous or asserted for an
              improper purpose, and not in any other circumstances. You understand that, absent this mandatory
              arbitration provision, you (and we) would have the right to sue in court and have a jury trial. You (and
              we) further understand that, in some instances, the costs of arbitration could exceed the costs of
              litigation and the right to discovery may be more limited in arbitration than in court.
            </li>
            <li>
              <u>Exceptions to Agreement to Arbitrate</u>. Either you (or we) may assert claims, if they qualify, in
              small claims court in New York, New York or any other U.S. county in which we’ve a principal business
              address, in the event that you and we’re unable to first resolve the dispute informally. Additionally,
              either party may bring a lawsuit solely for injunctive or equitable relief without first engaging in
              arbitration or the informal dispute-resolution process described above.
            </li>
          </TabbedList>
          <p>
            BY AGREEING TO THIS ARBITRATION AGREEMENT, YOU ARE GIVING UP YOUR RIGHT TO GO TO COURT, INCLUDING YOUR RIGHT
            TO A JURY TRIAL AND TO PARTICIPATE IN A CLASS ACTION. YOU UNDERSTAND THAT BY AGREEING TO THIS ARBITRATION
            AGREEMENT AND CLASS ACTION WAIVER, YOU MAY ONLY BRING CLAIMS AGAINST CAPSULE AND THE CAPSULE PARTIES IN AN
            INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS ACTION OR REPRESENTATIVE
            PROCEEDING. IF YOU DO NOT AGREE TO ARBITRATION, YOU SHOULD OPT OUT OF THIS ARBITRATION PROVISION PER THE
            TERMS. OTHERWISE, YOU ARE NOT AUTHORIZED TO USE THE SERVICES IN ANY WAY. If a claim, dispute, or controversy
            is brought that is found by a court to be excluded from the scope of these arbitration provisions, you and
            we agree to waive, to the fullest extent allowed by law, any trial by jury. You agree that the arbitration
            provision is made pursuant to a transaction involving interstate commerce and shall be governed by the
            Federal Arbitration Act, and not by any state law concerning arbitration
          </p>
        </li>

        <li>
          <h2 className="font-up-2">13. General Terms</h2>
          <p>
            <strong>Complete Agreement.</strong> You and Capsule agree that these Terms, together with the{' '}
            <LinkWithQuery to={privacyPath}>Privacy Policy</LinkWithQuery>, are the complete and exclusive statement of
            the mutual understanding between you and Capsule with respect to the Services, and supersede all previous
            written and oral agreements, communications and other understandings relating to the subject matter of these
            Terms.
          </p>
          <p>
            <strong>Headings.</strong> The headings of the sections and paragraphs of these Terms are for convenience
            only and don’t form a part of these Terms. The headings in no way limit, describe, modify, interpret, or
            construe the meaning, scope, or intent of these Terms.
          </p>
          <p>
            <strong>Severability.</strong> If any provision of these Terms is found to be unenforceable or invalid, that
            provision will be limited or eliminated, to the minimum extent necessary, so that these Terms shall
            otherwise remain in full force and effect and enforceable.
          </p>
          <p>
            <strong>No Waiver; No Modification.</strong> Capsule’s failure to insist upon or enforce strict performance
            of any provision of these Terms shall not be construed as a waiver of any provision or right. Any course of
            conduct between you and Capsule shall not act to modify any provision of these Terms.
          </p>
          <p>
            <strong>Assignment.</strong> You may not assign, delegate, or transfer these Terms or your rights or
            obligations hereunder in any way (by operation of law or otherwise) without Capsule’s prior written consent.
            Capsule may assign its rights and duties hereunder to any third party at any time without notice to you.
          </p>
          <p>
            <strong>No Third-Party Beneficiaries.</strong> Except as specifically set forth in these Terms, no third
            party shall have any right to enforce any right or enjoy any benefit that is created or established under
            these Terms.
          </p>
          <p>
            <strong>Notices.</strong> We may deliver notice to you concerning these Terms by placing a notice on the
            Services, by sending you an email, or by some other manner. You agree that all agreements, notices, and
            other communications that we provide to you electronically satisfy any legal requirement that such
            communications be in writing. Any notice to Capsule that is required or permitted by these Terms must be in
            writing and must be sent by email to legal@capsule.com for such notice to be deemed effective.
          </p>
          <p>
            <strong>Healthcare Law Compliance.</strong> Capsule is committed to maintaining high standards of integrity
            and compliance with applicable healthcare laws, including collaborating and cooperating with federal and
            state agencies to deter fraud, waste, and abuse. If you suspect any instances of fraud, waste, or abuse
            related to the Services or Medicare Part D, we encourage you to report it to us immediately. Your reports
            help us maintain compliance with CMS guidelines and ensure that resources are used appropriately for patient
            care.
          </p>
          <p>To report suspected fraud, waste, or abuse, you can:</p>
          <TabbedList>
            <li>Call our confidential hotline at 866-302-0212</li>
            <li>Email us at anonymousreporting@capsule.com</li>
            <li>
              Submit a report online through our secure portal (
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSci6ywSF_Nt0hTy0xkvXwyS9KokGCjx7I5VOOeo-DnM7XP_nA/viewform"
                target="_blank"
                rel="noopener noreferrer"
              >
                link
              </a>
              )
            </li>
          </TabbedList>
          <p>Your privacy is important to us. All reports are confidential and may be made anonymously.</p>
          <p>
            <strong>Copyright Infringement Complaints.</strong> Capsule respects the intellectual property of others and
            we ask you to do the same. The Digital Millennium Copyright Act of 1998 (DMCA) protects copyright owners who
            believe that material appearing on the Internet infringes their rights under U.S. copyright law. If you
            believe in good faith that materials available on the Service infringes your rights under U.S. copyright
            law, you (or your agent) may send us a written notice by mail or other legally permissible method,
            requesting that we remove such material or block access to it. If you believe in good faith that someone has
            wrongly filed a notice of copyright infringement against you, the DMCA permits you to send us a
            counter-notice. Notices and counter-notices must be sent in writing and meet the then-current statutory
            requirements imposed by the DMCA (see http://www.copyright.gov/ for details). It is our policy, in
            appropriate circumstances, to terminate the accounts of customers who are repeat infringers or who are
            repeatedly charged with infringement. Our agent for notice of claims of copyright infringement can be
            reached by email at legal@capsule.com or mail at {address} (Attention: Copyright Agent).
          </p>
        </li>
      </TopLevelOrderedList>
    </Main>
  );
};
